import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogShell,
  DialogShellHeader,
  DialogShellBody,
  Label,
  ReadOnlyField,
  Skeleton,
  STEP_FIELD_ICONS,
  getUserFullName,
  type ViewFetchState
} from '@bliks-io/ui';
import { formatDateTime } from '@/lib/formatters';
import { DialogLoadStateBody } from '@/components/dialogs/DialogLoadStateBody';
import { getEffectiveAccountable } from './effective-accountable';

type Person = {
  firstName: string;
  lastName: string;
  email?: string;
};

type Process = {
  _id: string;
  name: string;
  description?: string;
  owner?: Person;
  createdBy?: Person;
  accountable?: Person | null;
  department?: {
    name: string;
    manager?: Person;
  };
  apqcEquivalentId?: string;
  createdAt?: string;
  updatedAt?: string;
};

type ProcessDetailsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  process?: Process;
  /** Why `process` is missing; a loaded process always wins over it. */
  loadState?: ViewFetchState;
};

export function ProcessDetailsDialog({
  open,
  onOpenChange,
  process,
  loadState = { status: 'pending' }
}: ProcessDetailsDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogShell size="lg">
        <DialogShellHeader
          icon={<STEP_FIELD_ICONS.taskDescription className="h-5 w-5" />}
          iconTone="neutral"
          title={t('processPage.processNameDesc')}
          subtitle={t('viralRecording.labels.processDetailsOverview')}
          onClose={() => onOpenChange(false)}
        />
        <DialogShellBody>
          {process ? (
            <ProcessDetailsFields process={process} />
          ) : (
            <DialogLoadStateBody
              loadState={loadState}
              notFoundKey="errors.process.notFound"
              skeleton={<ProcessDetailsSkeleton />}
            />
          )}
        </DialogShellBody>
      </DialogShell>
    </Dialog>
  );
}

function ProcessDetailsFields({ process }: { process: Process }) {
  const { t } = useTranslation();
  const accountable = getEffectiveAccountable(process.accountable, process.department?.manager);

  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <div className="space-y-4">
        <div className="space-y-2">
          <Label className="text-sm font-medium">{t('viralRecording.labels.processName')}</Label>
          <ReadOnlyField value={process.name || '-'} />
        </div>

        <div className="space-y-2">
          <Label className="text-sm font-medium">{t('viralRecording.labels.description')}</Label>
          <ReadOnlyField
            value={process.description || t('viralRecording.labels.noDescriptionAvailable')}
          />
        </div>
      </div>

      {/* Organization Information */}
      <div className="space-y-4 pt-4 border-t">
        <h3 className="text-sm font-medium text-muted-foreground">
          {t('viralRecording.labels.organizationInfo')}
        </h3>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Owner */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t('viralRecording.labels.owner')}</Label>
            <ReadOnlyField
              value={process.owner ? `${process.owner.firstName} ${process.owner.lastName}` : '-'}
            />
            {process.owner?.email && (
              <p className="text-xs text-muted-foreground">{process.owner.email}</p>
            )}
          </div>

          {/* Accountable */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t('processes.Accountable')}</Label>
            <ReadOnlyField
              value={
                accountable ? getUserFullName(accountable.firstName, accountable.lastName) : '-'
              }
            />
            {accountable?.email && (
              <p className="text-xs text-muted-foreground">{accountable.email}</p>
            )}
          </div>

          {/* Creator */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t('viralRecording.labels.createdBy')}</Label>
            <ReadOnlyField
              value={
                process.createdBy
                  ? `${process.createdBy.firstName} ${process.createdBy.lastName}`
                  : '-'
              }
            />
            {process.createdBy?.email && (
              <p className="text-xs text-muted-foreground">{process.createdBy.email}</p>
            )}
          </div>
        </div>

        {/* Department */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">{t('viralRecording.labels.department')}</Label>
          <ReadOnlyField value={process.department?.name || '-'} />
        </div>
      </div>

      {/* Timestamps */}
      <div className="space-y-4 pt-4 border-t">
        <h3 className="text-sm font-medium text-muted-foreground">
          {t('viralRecording.labels.timestamps')}
        </h3>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Created At */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t('viralRecording.labels.createdAt')}</Label>
            <ReadOnlyField mono value={formatDateTime(process.createdAt)} />
          </div>

          {/* Updated At */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t('viralRecording.labels.updatedAt')}</Label>
            <ReadOnlyField mono value={formatDateTime(process.updatedAt)} />
          </div>
        </div>
      </div>
    </div>
  );
}

function ProcessDetailsSkeleton() {
  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <Skeleton className="h-9" />
        <Skeleton className="h-9" />
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Skeleton className="h-9" />
        <Skeleton className="h-9" />
        <Skeleton className="h-9" />
      </div>
      <Skeleton className="h-9" />
    </div>
  );
}
